import * as THREE from 'three';

/**
 * Final grade — runs in display-referred space, *after* OutputPass has done the
 * ACES tone map and the sRGB encode. Nothing here tone maps again.
 *
 * ASC-CDL (slope / offset / power) is the grading primitive every colourist and
 * every film pipeline actually uses, and it is defined on video-range code
 * values, not on linear light — which is exactly what we have at this point.
 * On top of it: a channel mixer for a little dye cross-talk, a saturation trim,
 * a luminance-keyed split tone (warm highlights, cool-teal shadows), then the
 * two things that must be last because they are properties of the *print* and
 * not of the scene: vignette and grain.
 */
export const GradeShader = {
  name: 'GradeShader',

  uniforms: {
    tDiffuse: { value: null },

    // ASC-CDL
    uSlope: { value: new THREE.Vector3(1.0, 1.0, 1.0) },
    uOffset: { value: new THREE.Vector3(0.0, 0.0, 0.0) },
    uPower: { value: new THREE.Vector3(1.0, 1.0, 1.0) },

    uMixer: { value: new THREE.Matrix3() },
    uSaturation: { value: 1.0 },
    uShadowDesat: { value: 0.0 },
    uShadowTint: { value: new THREE.Vector3(0, 0, 0) },
    uHighTint: { value: new THREE.Vector3(0, 0, 0) },

    uVignette: { value: 0.3 },
    uGrain: { value: 0.03 },
    uGrainSeed: { value: new THREE.Vector2() },

    uDamage: { value: 0.0 },
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    varying vec2 vUv;

    uniform sampler2D tDiffuse;
    uniform vec3 uSlope;
    uniform vec3 uOffset;
    uniform vec3 uPower;
    uniform mat3 uMixer;
    uniform float uSaturation;
    uniform float uShadowDesat;
    uniform vec3 uShadowTint;
    uniform vec3 uHighTint;
    uniform float uVignette;
    uniform float uGrain;
    uniform vec2 uGrainSeed;
    uniform float uDamage;

    const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

    // Hash without sine — stable across GPUs, no banding, no periodicity at 4K.
    float hash12(vec2 p) {
      vec3 p3 = fract(vec3(p.xyx) * 0.1031);
      p3 += dot(p3, p3.yzx + 33.33);
      return fract((p3.x + p3.y) * p3.z);
    }

    void main() {
      vec3 c = texture2D(tDiffuse, vUv).rgb;

      /* ---- ASC-CDL --------------------------------------------------- */
      c = clamp(c * uSlope + uOffset, 0.0, 1.0);
      c = pow(c, uPower);

      /* ---- split tone ------------------------------------------------ */
      float l = dot(c, LUMA);
      float shadowW = 1.0 - smoothstep(0.0, 0.55, l);
      float highW = smoothstep(0.40, 1.0, l);
      c += uShadowTint * shadowW + uHighTint * highW;

      /* ---- channel mixer + saturation -------------------------------- */
      c = clamp(uMixer * c, 0.0, 1.0);
      c = mix(vec3(dot(c, LUMA)), c, uSaturation);

      // Film loses chroma in the toe: deep shadows go grey-blue, never vivid.
      // Renderers do the opposite — they hand you fully saturated sky-ambient
      // in every shadow, which is the tell that an image was computed.
      c = mix(c, vec3(dot(c, LUMA)), uShadowDesat * shadowW);

      float r = length(vUv - 0.5) * 1.41421356;

      /* ---- damage: bleach the frame, bruise the edges ----------------- */
      if (uDamage > 0.001) {
        // The red has to stay out of the middle third. However badly hurt the
        // player is, they still have to be able to see what they are shooting
        // at — a hurt overlay that covers the sights is a bug, not a mood.
        float e = smoothstep(0.30, 1.02, r);
        c = mix(c, vec3(dot(c, LUMA)), 0.50 * uDamage);
        c = mix(c, vec3(0.44, 0.020, 0.014), e * uDamage * 0.62);
        c *= 1.0 - 0.10 * uDamage;
      }

      /* ---- vignette: monotonic power falloff, so there is no ring ----- */
      c *= 1.0 - uVignette * pow(clamp(r, 0.0, 1.0), 2.6);

      /* ---- grain ------------------------------------------------------
         Film grain peaks in the low mids, not in the blacks: below the toe
         there is no exposed silver left to be grainy, and above the shoulder
         the emulsion is saturated. A flat "more grain in shadows" ramp puts
         its loudest noise exactly where the image is darkest, which crawls.  */
      float g = hash12(gl_FragCoord.xy + uGrainSeed) - 0.5;
      float lum = dot(c, LUMA);
      float gw = smoothstep(0.0, 0.09, lum) * mix(1.0, 0.10, smoothstep(0.06, 0.70, lum));
      c += g * uGrain * gw;

      gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
    }
  `,
};
